/**
 * Admin Service
 * 
 * Service for handling admin-related API calls (location assignments).
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export type AdminLevel = "nasional" | "province" | "city";
export type AdminLevelType = "nasional" | "province" | "city";
export type AdminPrivilege = 
  | "manage_users"
  | "manage_user_reset_password"
  | "manage_user_set_email"
  | "manage_locations"
  | "manage_payments"
  | "manage_blocks"
  | "manage_silentkey"
  | "manage_jabatan"
  | "manage_admin_assignments"
  | "view_reports"
  | "manage_transactions"
  | "manage_transactions_plan"
  | "create_transaction_plan"
  | "manage_historical_transfer"
  | "manage_transfer_plans"
  | "create_transaction_bank_account";

export interface AdminLocationAssignment {
  _id: string;
  /** Omitted on GET /me and login; present on admin assignment list APIs. */
  admin?: {
    _id: string;
    callsign: string;
    nama_lengkap: string;
    email: string;
    provinsi: string;
    kabupaten: string;
  };
  level: AdminLevel;
  provinces: string[];
  cities: string[];
  privileges?: AdminPrivilege[];
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLocationAssignmentInput {
  admin: string;
  level: AdminLevel;
  provinces?: string[];
  cities?: string[];
  privileges?: AdminPrivilege[];
  isActive?: boolean;
  notes?: string;
}

export interface UpdateLocationAssignmentInput {
  provinces?: string[];
  cities?: string[];
  privileges?: AdminPrivilege[];
  isActive?: boolean;
  notes?: string;
}

export interface LocationAssignmentListResponse {
  data: AdminLocationAssignment[];
  total: number;
}

export interface LocationAssignmentResponse {
  data: AdminLocationAssignment;
}

class AdminService {
  /**
   * Get all location assignments with optional filters
   * @param filters - Optional filters (admin, level, isActive, province, city)
   * @returns Promise with array of assignments
   */
  async getAllLocationAssignments(filters?: {
    admin?: string;
    level?: AdminLevel;
    isActive?: boolean;
    province?: string;
    city?: string;
  }): Promise<LocationAssignmentListResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.admin) params.append("admin", filters.admin);
      if (filters?.level) params.append("level", filters.level);
      if (filters?.isActive !== undefined) params.append("isActive", filters.isActive.toString());
      if (filters?.province) params.append("province", filters.province);
      if (filters?.city) params.append("city", filters.city);

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/admin/location-assignments?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: LocationAssignmentListResponse = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching location assignments:", error);
      throw error;
    }
  }

  /**
   * Get location assignment by ID
   * @param id - Assignment ID
   * @returns Promise with assignment
   */
  async getLocationAssignmentById(id: string): Promise<AdminLocationAssignment> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/admin/location-assignments/${id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: LocationAssignmentResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching location assignment:", error);
      throw error;
    }
  }

  /**
   * Get all location assignments for a specific admin
   * @param adminId - Admin user ID
   * @returns Promise with array of assignments
   */
  async getLocationAssignmentsByAdmin(adminId: string): Promise<AdminLocationAssignment[]> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/admin/location-assignments/admin/${adminId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: LocationAssignmentListResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching location assignments by admin:", error);
      throw error;
    }
  }

  /**
   * Create new location assignment
   * @param input - Assignment data
   * @returns Promise with created assignment
   */
  async createLocationAssignment(input: CreateLocationAssignmentInput): Promise<AdminLocationAssignment> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/admin/location-assignments`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: LocationAssignmentResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error creating location assignment:", error);
      throw error;
    }
  }

  /**
   * Update location assignment by ID
   * @param id - Assignment ID
   * @param input - Update data
   * @returns Promise with updated assignment
   */
  async updateLocationAssignment(id: string, input: UpdateLocationAssignmentInput): Promise<AdminLocationAssignment> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/admin/location-assignments/${id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: LocationAssignmentResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error updating location assignment:", error);
      throw error;
    }
  }

  /**
   * Delete location assignment by ID
   * @param id - Assignment ID
   * @returns Promise with void
   */
  async deleteLocationAssignment(id: string): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/admin/location-assignments/${id}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error deleting location assignment:", error);
      throw error;
    }
  }

  /**
   * Fetch all admin levels
   */
  async fetchAdminLevels(level?: AdminLevelType): Promise<AdminLevelItem[]> {
    try {
      const params = new URLSearchParams();
      if (level) {
        params.append("level", level);
      }

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/admin-levels?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: AdminLevelListResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching admin levels:", error);
      throw error;
    }
  }

  /**
   * Fetch admin level by ID
   */
  async fetchAdminLevelById(id: string): Promise<AdminLevelItem> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/admin-levels/${id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: AdminLevelResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching admin level:", error);
      throw error;
    }
  }

  /**
   * Create admin level
   */
  async createAdminLevel(input: CreateAdminLevelInput): Promise<AdminLevelItem> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/admin-levels`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: AdminLevelResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error creating admin level:", error);
      throw error;
    }
  }

  /**
   * Update admin level
   */
  async updateAdminLevel(id: string, input: UpdateAdminLevelInput): Promise<AdminLevelItem> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/admin-levels/${id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: AdminLevelResponse = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error updating admin level:", error);
      throw error;
    }
  }

  /**
   * Delete admin level
   */
  async deleteAdminLevel(id: string): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/management/admin-levels/${id}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error deleting admin level:", error);
      throw error;
    }
  }
}

// Admin Level types and interfaces
export interface AdminLevelItem {
  _id: string;
  name: string;
  description?: string;
  level: AdminLevelType;
  order: number;
  defaultPrivileges?: AdminPrivilege[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdminLevelInput {
  name: string;
  description?: string;
  level: AdminLevelType;
  order: number;
  defaultPrivileges?: AdminPrivilege[];
}

export interface UpdateAdminLevelInput {
  name?: string;
  description?: string;
  level?: AdminLevelType;
  order?: number;
  defaultPrivileges?: AdminPrivilege[];
}

export interface AdminLevelListResponse {
  data: AdminLevelItem[];
}

export interface AdminLevelResponse {
  data: AdminLevelItem;
}

export const adminService = new AdminService();

